import { useEffect, useState } from "react";
import { Bug, Check, ChevronDown, Inbox, Lightbulb, Loader2, MessageCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, Empty, btn } from "@/components/common";
import { cn } from "@/lib/utils";

const META = {
  bug: ["Bug", "bg-red-50 text-red-600", Bug],
  suggestion: ["Suggestion", "bg-amber-50 text-amber-700", Lightbulb],
  feedback: ["Feedback", "bg-sky-50 text-sky-600", MessageCircle],
};
const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; } };

function Stat({ label, value, testid }) {
  return <Card className="p-5" data-testid={testid}><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p><p className="mt-2 font-display text-3xl font-bold text-navy-900">{value}</p></Card>;
}

function Dot({ testid, color = "red" }) {
  const [ping, solid] = color === "orange" ? ["bg-orange-400", "bg-orange-500"] : ["bg-red-400", "bg-red-500"];
  return <span data-testid={testid} title="New — not yet seen" className="relative flex h-2.5 w-2.5"><span className={cn("absolute inline-flex h-full w-full animate-ping rounded-full opacity-75", ping)} /><span className={cn("relative inline-flex h-2.5 w-2.5 rounded-full", solid)} /></span>;
}

function Item({ f, busy, resolve }) {
  const [label, cls, Icon] = META[f.type] || META.feedback;
  return (
    <Card className="p-4 sm:p-5" data-testid={`feedback-item-${f.id}`}>
      <div className="flex items-start gap-3">
        <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-full", cls)}><Icon className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", cls)}>{label}</span>
            <span className="text-sm font-semibold text-navy-900">{f.submitterName}</span>
            <span className="text-xs text-slate-400">{f.submitterKind === "client" ? "Client" : "Agent"} · {fmt(f.createdAt)}</span>
            {f.status === "resolved" && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600">Resolved</span>}
          </div>
          <p className="mt-1.5 whitespace-pre-line text-sm text-slate-700">{f.message}</p>
          {f.page && <p className="mt-1 text-xs text-slate-400">Page: {f.page}</p>}
        </div>
        {f.status !== "resolved" && <button disabled={busy === f.id} onClick={() => resolve(f.id)} data-testid={`feedback-resolve-${f.id}`} className={`${btn.ghost} h-8`}><Check className="h-3.5 w-3.5" />Resolve</button>}
      </div>
    </Card>
  );
}

function Folder({ title, icon: Icon, count, dot, dotColor = "red", defaultOpen = false, testid, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card data-testid={testid}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`} className="flex w-full items-center gap-2.5 px-5 py-4 text-left hover:bg-slate-50/60">
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-slate-400 transition-transform", open && "rotate-180")} />
        {Icon && <Icon className="h-4 w-4 shrink-0 text-slate-500" />}
        <span className="text-sm font-semibold text-navy-900">{title}</span>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">{count}</span>
        {dot && <Dot testid={`${testid}-dot`} color={dotColor} />}
      </button>
      {open && <div className="space-y-3 border-t border-slate-100 bg-slate-50/40 p-4 sm:p-5">{children}</div>}
    </Card>
  );
}

export default function FeedbackTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/feedback").then(({ data }) => { setData(data); if ((data.items || []).some((i) => !i.seenByAdmin)) api.post("/feedback/seen").catch(() => {}); }).catch(() => { toast.error("Couldn't load feedback — please refresh"); setData({ items: [], stats: { total: 0, bug: 0, suggestion: 0, feedback: 0 } }); });
  useEffect(() => { load(); }, []);
  const resolve = async (id) => { setBusy(id); try { await api.post(`/feedback/${id}/resolve`); await load(); } catch { toast.error("Couldn't resolve — please try again"); } setBusy(null); };

  if (!data) return <Card><div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy-700" /></div></Card>;
  const { items, stats } = data;

  const bugs = items.filter((i) => i.type === "bug");
  const activeBugs = bugs.filter((b) => b.status !== "resolved");
  const resolvedBugs = bugs.filter((b) => b.status === "resolved");
  // Suggestions + feedback share one folder; keep incomplete near the top (stable sort preserves newest-first within each group).
  const ideas = items.filter((i) => i.type === "suggestion" || i.type === "feedback").slice().sort((a, b) => (a.status === "resolved" ? 1 : 0) - (b.status === "resolved" ? 1 : 0));
  const newBugs = activeBugs.some((b) => !b.seenByAdmin);
  const newIdeas = ideas.some((i) => !i.seenByAdmin);

  return (
    <div className="space-y-4" data-testid="communication-feedback-page">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Total" value={stats.total} testid="feedback-stat-total" />
        <Stat label="Bugs" value={stats.bug} testid="feedback-stat-bug" />
        <Stat label="Suggestions" value={stats.suggestion} testid="feedback-stat-suggestion" />
        <Stat label="Feedback" value={stats.feedback} testid="feedback-stat-feedback" />
      </div>

      {items.length === 0 ? (
        <Card><Empty icon={Inbox} title="No feedback yet" text="Bug reports, suggestions and feedback submitted from either portal will show up here." /></Card>
      ) : (
        <>
          {/* Bugs — open ones always listed as-is; red dot when any are open */}
          <div className="space-y-3" data-testid="bugs-section">
            <div className="flex items-center gap-2.5 px-1">
              <Bug className="h-4 w-4 text-red-500" />
              <h3 className="text-sm font-semibold text-navy-900">Open bugs</h3>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">{activeBugs.length}</span>
              {newBugs && <Dot testid="bugs-new-dot" />}
            </div>
            {activeBugs.length === 0
              ? <Card className="p-5"><p className="text-sm text-slate-500" data-testid="bugs-empty">No open bug reports. 🎉</p></Card>
              : activeBugs.map((f) => <Item key={f.id} f={f} busy={busy} resolve={resolve} />)}
            {resolvedBugs.length > 0 && (
              <Folder title="Resolved bugs" icon={Check} count={resolvedBugs.length} testid="resolved-bugs-folder">
                {resolvedBugs.map((f) => <Item key={f.id} f={f} busy={busy} resolve={resolve} />)}
              </Folder>
            )}
          </div>

          {/* Suggestions & Feedback — stay in one folder, incomplete near the top; red dot when new */}
          <Folder title="Suggestions & Feedback" icon={Sparkles} count={ideas.length} dot={newIdeas} dotColor="orange" testid="ideas-folder">
            {ideas.length === 0
              ? <p className="text-sm text-slate-500">No suggestions or feedback yet.</p>
              : ideas.map((f) => <Item key={f.id} f={f} busy={busy} resolve={resolve} />)}
          </Folder>
        </>
      )}
    </div>
  );
}
